"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { MapPin, Search, Loader2, Globe, Wifi, Copy, Check, RefreshCw } from "lucide-react"
import { toast } from "sonner"

interface IpInfoResult {
  ip: string
  type: "IPv4" | "IPv6"
  location?: {
    country?: string
    countryCode?: string
    region?: string
    regionCode?: string
    city?: string
    zip?: string
    latitude?: number
    longitude?: number
    timezone?: string
  }
  network?: {
    asn?: string
    org?: string
    isp?: string
  }
  isVpn?: boolean
  isProxy?: boolean
  isHosting?: boolean
  timing: number
}

export function IpInfo() {
  const [ip, setIp] = useState("")
  const [result, setResult] = useState<IpInfoResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    fetch("/api/tools/ip-info", { signal: controller.signal })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          setError(data.error?.message || "Failed to get IP info")
          return
        }

        setResult(data.data)
        setIp(data.data.ip)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setError("Failed to get IP information")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })

    return () => controller.abort()
  }, [])

  const fetchMyIp = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch("/api/tools/ip-info")
      const data = await response.json()

      if (!data.success) {
        setError(data.error?.message || "Failed to get IP info")
        return
      }

      setResult(data.data)
      setIp(data.data.ip)
    } catch {
      setError("Failed to get IP information")
    } finally {
      setLoading(false)
    }
  }

  const handleLookup = async () => {
    if (!ip.trim()) {
      toast.error("Please enter an IP address")
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch("/api/tools/ip-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip }),
      })

      const data = await response.json()

      if (!data.success) {
        setError(data.error?.message || "Failed to lookup IP")
        toast.error(data.error?.message || "Failed to lookup IP")
        return
      }

      setResult(data.data)
      toast.success("IP information retrieved")
    } catch {
      setError("Failed to lookup IP")
      toast.error("Failed to lookup IP")
    } finally {
      setLoading(false)
    }
  }

  const copyIp = () => {
    if (result?.ip) {
      navigator.clipboard.writeText(result.ip)
      setCopied(true)
      toast.success("IP copied to clipboard")
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLookup()
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            IP Geolocation
          </CardTitle>
          <CardDescription>
            Get geolocation and network information for any IP address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ip">IP Address</Label>
            <div className="flex gap-2">
              <Input
                id="ip"
                placeholder="8.8.8.8"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button onClick={handleLookup} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Lookup</span>
              </Button>
              <Button variant="outline" onClick={fetchMyIp} disabled={loading}>
                <RefreshCw className="h-4 w-4" />
                <span className="ml-2 hidden sm:inline">My IP</span>
              </Button>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* IP Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  IP Address
                </CardTitle>
                <Badge>{result.type}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                <code className="text-2xl font-bold">{result.ip}</code>
                <Button size="icon" variant="ghost" onClick={copyIp}>
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="flex gap-2 mt-4">
                {result.isProxy && (
                  <Badge variant="destructive">Proxy</Badge>
                )}
                {result.isVpn && (
                  <Badge variant="destructive">VPN/Mobile</Badge>
                )}
                {result.isHosting && (
                  <Badge variant="secondary">Hosting/DC</Badge>
                )}
                {!result.isProxy && !result.isVpn && !result.isHosting && (
                  <Badge variant="outline">Residential</Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.location?.country && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Country</span>
                  <span className="font-medium">
                    {result.location.countryCode && (
                      <span className="mr-2">
                        {getFlagEmoji(result.location.countryCode)}
                      </span>
                    )}
                    {result.location.country}
                  </span>
                </div>
              )}
              {result.location?.region && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Region</span>
                  <span className="font-medium">{result.location.region}</span>
                </div>
              )}
              {result.location?.city && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">City</span>
                  <span className="font-medium">{result.location.city}</span>
                </div>
              )}
              {result.location?.zip && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ZIP Code</span>
                  <span className="font-medium">{result.location.zip}</span>
                </div>
              )}
              {result.location?.timezone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Timezone</span>
                  <span className="font-medium">{result.location.timezone}</span>
                </div>
              )}
              {result.location?.latitude && result.location?.longitude && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Coordinates</span>
                  <span className="font-mono text-sm">
                    {result.location.latitude.toFixed(4)}, {result.location.longitude.toFixed(4)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Network */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wifi className="h-5 w-5" />
                Network Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                {result.network?.isp && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground uppercase mb-1">ISP</p>
                    <p className="font-medium truncate">{result.network.isp}</p>
                  </div>
                )}
                {result.network?.org && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Organization</p>
                    <p className="font-medium truncate">{result.network.org}</p>
                  </div>
                )}
                {result.network?.asn && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground uppercase mb-1">ASN</p>
                    <p className="font-mono text-sm truncate">{result.network.asn}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}
